// (2026-06-09) MAESTRO de Proveedores — FORMULARIO como PÁGINA (crear/editar).
//
// Replica la ESTRUCTURA/UX del maestro de Clientes (FormClients.js): el form
// es una PÁGINA completa (no modal), con rutas:
//   - /compras/proveedores/create
//   - /compras/proveedores/edit?id=<id encriptado>
// El id a editar se lee igual que el maestro: query param ?id= y se
// desencripta con decryptText(...) de ../../controllers/api. Tras guardar,
// navega de vuelta a la lista (/compras/proveedores).
//
// CONSERVA toda la lógica/llamadas API existentes del módulo de Compras
// (createProveedor / updateProveedor / getProveedor de
// ../../controllers/api_compras): mismos endpoints, mismos campos de payload
// y misma validación (Nombre y RIF obligatorios).
//
// NOVEDAD: sección de CUENTAS BANCARIAS 1:M. Reemplaza los campos sueltos
// banco/numero_cuenta por un editor donde se pueden AGREGAR y QUITAR múltiples
// cuentas (campos: banco, numero_cuenta, tipo_cuenta, titular). Al guardar se
// envía el arreglo `cuentas` en el payload. En modo edición se precargan las
// cuentas del proveedor.
//
// Reusa los primitivos de ./ui.js. NADA de @mui. Conserva AdminLayout y
// react-toastify.

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { decryptText } from "../../controllers/api";
import {
  getProveedor, createProveedor, updateProveedor,
} from "../../controllers/api_compras";
import {
  MONEDAS, handleApiError, upper,
  maskRif, maskPhone, validateFormatRif, validateFormatPhone, validateFormatEmail,
} from "./comprasShared";
import {
  Field, TextInput, SelectInput, Toggle, BtnPrimary, BtnGhost,
  IconBtn, Card, PageHeader, SectionTitle, Table, Th, Td,
} from "./ui";

// Mismo objeto base que el módulo existente (se conservan los campos del
// payload), pero banco/numero_cuenta sueltos quedan reemplazados por el
// arreglo `cuentas` (1:M).
// (2026-06-09) El campo "Tipo doc." se eliminó del formulario: el
// tipo_documento_identidad se deriva del primer carácter del RIF al guardar.
const EMPTY = {
  nombre: "", rif: "", categoria_fiscal: "",
  dias_credito: 0, moneda_designada_id: 3, telefono: "", email: "",
  direccion: "", activo: true,
};

// Deriva el tipo de documento (J/V/E/G/P/C) del primer carácter del RIF.
// Para cédula sin prefijo (solo dígitos) se asume V (persona natural).
//
// (2026-08-03, ronda 2 / T1) 'C' (Comunal) añadida a la clase. NO es cosmético:
// esta función PERSISTE `tipo_documento_identidad`, y con la clase antigua un
// RIF 'C-12345678-9' caía al `: "V"` del fallback y se guardaba como tipo 'V'
// — el tipo de documento quedaba CONTRADICIENDO al propio RIF, en silencio y
// sin error. El fallback a "V" se conserva SOLO para su caso legítimo: cédula
// sin prefijo (el valor empieza por dígito).
const tipoDocDesdeRif = (rif) => {
  const c = String(rif || "").trim().charAt(0).toUpperCase();
  return /[VEJGPC]/.test(c) ? c : "V";
};

// Cuenta bancaria vacía para el editor 1:M.
const EMPTY_CUENTA = { banco: "", numero_cuenta: "", tipo_cuenta: "", titular: "" };

export default function ProveedoresForm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Igual que FormClients: el id a editar viaja como query param ?id= y se
  // desencripta. Si no hay id → modo creación.
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encryptedId = queryParams.get("id");
  const realId = encryptedId != null ? decryptText(encryptedId) : null;
  const isEdit = !!realId;

  const [form, setForm] = useState(EMPTY);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isEdit) {
          const data = await getProveedor(realId);
          setForm({ ...EMPTY, ...data });
          // Precarga las cuentas del proveedor (si el backend las expone).
          setCuentas(Array.isArray(data.cuentas) ? data.cuentas.map((c) => ({ ...EMPTY_CUENTA, ...c })) : []);
        } else {
          setForm(EMPTY);
          setCuentas([]);
        }
      } catch (e) {
        handleApiError(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [encryptedId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setUp = (k) => (v) => setForm((f) => ({ ...f, [k]: upper(v) }));

  // --- Editor de cuentas bancarias (1:M) ---
  const addCuenta = () => setCuentas((cs) => [...cs, { ...EMPTY_CUENTA }]);
  const removeCuenta = (idx) => setCuentas((cs) => cs.filter((_, i) => i !== idx));
  const setCuentaField = (idx, key, val) =>
    setCuentas((cs) => cs.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));

  const redirectToList = () => navigate("/compras/proveedores");

  // (2026-06-09) Validación al estilo del maestro de Clientes
  // (components/Clients/FormClients.js): TODOS los inputs visibles deben tener
  // un valor válido asignado por el usuario; la ÚNICA sección que puede quedar
  // vacía es "Cuentas bancarias". Errores por campo + un toast con la lista.
  const validate = () => {
    const newErrors = {};
    const errorToast = [];
    if (!form.nombre) {
      newErrors.nombre = "Razón social es obligatoria";
      errorToast.push("- Razón social es obligatoria");
    }
    if (!form.rif) {
      newErrors.rif = "R.I.F. es obligatorio";
      errorToast.push("- R.I.F. es obligatorio");
    } else if (!validateFormatRif(form.rif)) {
      newErrors.rif = "R.I.F. no presenta un formato válido";
      errorToast.push("- R.I.F. no presenta un formato válido");
    }
    if (!form.categoria_fiscal) {
      newErrors.categoria_fiscal = "Categoría fiscal es obligatoria";
      errorToast.push("- Categoría fiscal es obligatoria");
    }
    if (
      form.dias_credito === "" || form.dias_credito === null || form.dias_credito === undefined ||
      isNaN(Number(form.dias_credito)) || Number(form.dias_credito) < 0
    ) {
      newErrors.dias_credito = "Días de crédito debe ser un número (0 o más)";
      errorToast.push("- Días de crédito debe ser un número válido (0 o más)");
    }
    if (!form.moneda_designada_id) {
      newErrors.moneda_designada_id = "Moneda designada es obligatoria";
      errorToast.push("- Moneda designada es obligatoria");
    }
    if (!form.telefono) {
      newErrors.telefono = "Teléfono es obligatorio";
      errorToast.push("- Teléfono es obligatorio");
    } else if (!validateFormatPhone(form.telefono)) {
      newErrors.telefono = "Teléfono no presenta un formato válido";
      errorToast.push("- Teléfono no presenta un formato válido (0000-0000000)");
    }
    if (!form.email) {
      newErrors.email = "Correo electrónico es obligatorio";
      errorToast.push("- Correo electrónico es obligatorio");
    } else if (!validateFormatEmail(form.email)) {
      newErrors.email = "Correo electrónico no presenta un formato válido";
      errorToast.push("- Correo electrónico no presenta un formato válido");
    }
    if (!form.direccion) {
      newErrors.direccion = "Dirección es obligatoria";
      errorToast.push("- Dirección es obligatoria");
    }
    // Cuentas bancarias: ÚNICA sección que puede quedar vacía. Si una fila
    // tiene algún dato, debe tener banco y número de cuenta.
    cuentas.forEach((c, i) => {
      const algo = c.banco || c.numero_cuenta || c.tipo_cuenta || c.titular;
      if (algo && (!c.banco || !c.numero_cuenta)) {
        newErrors[`cuenta_${i}`] = true;
        errorToast.push(`- Cuenta bancaria #${i + 1}: banco y número de cuenta son obligatorios`);
      }
    });
    setErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(
        <div>
          {errorToast.map((item, i) => (
            <span key={i} className="text-start">{item}<br /></span>
          ))}
        </div>
      );
    }
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setButtonDisabled(true);
    try {
      // Solo cuentas con al menos banco o número (descarta filas vacías).
      const cuentasLimpias = cuentas
        .map((c) => ({
          ...(c.id ? { id: c.id } : {}),
          banco: c.banco || "",
          numero_cuenta: c.numero_cuenta || "",
          tipo_cuenta: c.tipo_cuenta || "",
          titular: c.titular || "",
        }))
        .filter((c) => c.banco || c.numero_cuenta || c.tipo_cuenta || c.titular);

      // El tipo de documento se deriva del primer carácter del RIF (J/V/E/G/P).
      const payload = {
        ...form,
        tipo_documento_identidad: tipoDocDesdeRif(form.rif),
        cuentas: cuentasLimpias,
      };

      if (isEdit) {
        await updateProveedor(realId, payload);
        toast.success("Proveedor actualizado.", { onClose: redirectToList });
      } else {
        await createProveedor(payload);
        toast.success("Proveedor creado.", { onClose: redirectToList });
      }
    } catch (e) {
      handleApiError(e);
      setButtonDisabled(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
        <div className="mt-8 px-4 md:px-8 text-blueGray-500">Cargando...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <div className="mt-8 px-4 md:px-8">
        <Card>
          <PageHeader
            title={isEdit ? "Actualizar proveedor" : "Crear proveedor"}
            subtitle="Datos generales, contacto y cuentas bancarias del proveedor."
            icon="fa-truck-field"
            actions={
              <BtnGhost icon="fa-arrow-left" onClick={redirectToList}>
                Volver
              </BtnGhost>
            }
          />

          <div className="flex-auto px-4 lg:px-6 py-8 pt-2">
            {/* --- Datos generales --- */}
            <SectionTitle icon="fa-circle-info">Datos generales</SectionTitle>
            <div className="flex flex-wrap -mx-4">
              <Field label="Razón social *" className="w-full lg:w-6/12 px-4" error={errors.nombre}>
                <TextInput value={form.nombre} onChange={setUp("nombre")} placeholder="NOMBRE DEL PROVEEDOR" />
              </Field>
              <Field label="RIF *" className="w-full lg:w-6/12 px-4" error={errors.rif}>
                <TextInput
                  value={form.rif}
                  onChange={(v) => setForm((f) => ({ ...f, rif: maskRif(v, f.rif) }))}
                  placeholder="J-12345678-9"
                />
              </Field>
              <Field label="Categoría fiscal *" className="w-full lg:w-4/12 px-4" error={errors.categoria_fiscal}>
                <SelectInput value={form.categoria_fiscal || ""} onChange={set("categoria_fiscal")}>
                  <option value="">(seleccione)</option>
                  <option value="PNR">PNR — Persona Natural Residente</option>
                  <option value="PJD">PJD — Persona Jurídica Domiciliada</option>
                </SelectInput>
              </Field>
              <Field label="Días de crédito *" className="w-full lg:w-4/12 px-4" error={errors.dias_credito}>
                <TextInput type="number" value={form.dias_credito} onChange={set("dias_credito")} />
              </Field>
              <Field label="Moneda designada *" className="w-full lg:w-4/12 px-4" error={errors.moneda_designada_id}>
                <SelectInput value={form.moneda_designada_id || 3} onChange={set("moneda_designada_id")}>
                  {MONEDAS.map((m) => <option key={m.id} value={m.id}>{m.codigo}</option>)}
                </SelectInput>
              </Field>
            </div>

            {/* --- Contacto --- */}
            <SectionTitle icon="fa-address-book">Contacto</SectionTitle>
            <div className="flex flex-wrap -mx-4">
              <Field label="Teléfono *" className="w-full lg:w-4/12 px-4" error={errors.telefono}>
                <TextInput
                  type="tel"
                  value={form.telefono || ""}
                  onChange={(v) => setForm((f) => ({ ...f, telefono: maskPhone(v) }))}
                  placeholder="0000-0000000"
                />
              </Field>
              <Field label="Email *" className="w-full lg:w-4/12 px-4" error={errors.email}>
                <TextInput
                  type="email"
                  value={form.email || ""}
                  onChange={setUp("email")}
                  placeholder="correo@correo.com"
                />
              </Field>
              <Field label="Estado" className="w-full lg:w-4/12 px-4">
                <div className="py-2">
                  <Toggle checked={!!form.activo} onChange={set("activo")} label="Activo" />
                </div>
              </Field>
              <Field label="Dirección *" className="w-full px-4" error={errors.direccion}>
                <TextInput value={form.direccion || ""} onChange={setUp("direccion")} placeholder="CALLE, AVENIDA, TORRE Y/O EDIFICIO" />
              </Field>
            </div>

            {/* --- Cuentas bancarias (1:M) --- */}
            <SectionTitle icon="fa-building-columns">Cuentas bancarias</SectionTitle>
            <div className="px-4">
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={addCuenta}
                  className="bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 transition"
                >
                  <i className="fas fa-plus" aria-hidden="true" />
                  Agregar cuenta
                </button>
              </div>

              <Table>
                <thead>
                  <tr>
                    <Th>Banco</Th>
                    <Th>Nº de cuenta</Th>
                    <Th>Tipo de cuenta</Th>
                    <Th>Titular</Th>
                    <Th className="text-right">Acc.</Th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.length === 0 && (
                    <tr>
                      <Td colSpan={5} className="text-center text-blueGray-400 italic">
                        Sin cuentas. Use "Agregar cuenta" para añadir una.
                      </Td>
                    </tr>
                  )}
                  {cuentas.map((c, idx) => (
                    <tr key={idx} className="hover:bg-blueGray-50 transition-colors">
                      <Td>
                        <TextInput value={c.banco} onChange={(v) => setCuentaField(idx, "banco", upper(v))} placeholder="BANCO" />
                      </Td>
                      <Td>
                        <TextInput type="tel" value={c.numero_cuenta} onChange={(v) => setCuentaField(idx, "numero_cuenta", v)} placeholder="0000-0000-00-0000000000" />
                      </Td>
                      <Td>
                        <SelectInput value={c.tipo_cuenta || ""} onChange={(v) => setCuentaField(idx, "tipo_cuenta", v)}>
                          <option value="">(seleccione)</option>
                          <option value="CORRIENTE">CORRIENTE</option>
                          <option value="AHORRO">AHORRO</option>
                        </SelectInput>
                      </Td>
                      <Td>
                        <TextInput value={c.titular} onChange={(v) => setCuentaField(idx, "titular", upper(v))} placeholder="TITULAR DE LA CUENTA" />
                      </Td>
                      <Td className="text-right">
                        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                          <IconBtn icon="fa-trash-can" title="Quitar cuenta" color="text-red-600" onClick={() => removeCuenta(idx)} />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* --- Footer (Guardar / Cancelar) --- */}
            <div className="mt-8 flex justify-center gap-3">
              <BtnGhost onClick={redirectToList} disabled={buttonDisabled}>
                Cancelar
              </BtnGhost>
              <BtnPrimary icon="fa-floppy-disk" onClick={save} disabled={buttonDisabled}>
                {buttonDisabled ? (isEdit ? "Actualizando..." : "Guardando...") : (isEdit ? "Actualizar" : "Guardar")}
              </BtnPrimary>
            </div>
          </div>
        </Card>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </AdminLayout>
  );
}
